import {useState, useEffect, useCallback, useMemo} from "react";
import "./Swap.scss";
import SwapCurrencyInput from "view/components/swap/SwapCurrencyInput";
import { actionHandler } from "action/parseAction";
import {
  getSwapPairAction,
  getCurrencyBalanceAction,
  getSwapLimitAciton,
  getTokenAmountsOutAction,
  getTokenAmountsInAction,
  makeSwapTransactionAction,
} from "action/swap/public";
import { MainNetInfo, WrapToken } from "server/swap/type";
import { Currency } from "server/walletManage/type";
import { useSelector } from "react-redux";
import { account_info } from "reducer/account";
import { WEB3 } from "modules/web3/Web3";
import { WrapTokenWithCurrency } from "reducer/swap/type";
import { downArrow } from "view/assets/images";
import { formatNumber } from "utils/numberUtil";
import BigNumber from "bignumber.js";
import {useNavigate, useOutletContext} from "react-router-dom";

const INITIAL_LIMIT = { min: 0, max: 0 }

function Swap() {
  // Redux
  const account = useSelector(account_info);

  const navigate = useNavigate();

  const {swapData}: {swapData: {
    mainNetInfo: MainNetInfo;
    wrapTokens: Array<WrapTokenWithCurrency>;
  }} = useOutletContext();

  // State
  const [swapPairs, setSwapPairs] = useState<Array<WrapTokenWithCurrency>>([]);

  const [swapState, setSwapState] = useState<{
    userInsert: "IN" | "OUT" | "NOTHING";
    isReady: boolean;
  }>({
    userInsert: "NOTHING",
    isReady: false,
  });

  const [tokenIn, setTokenIn] = useState<WrapTokenWithCurrency | undefined>();
  const [tokenInBalance, setTokenInBalance] = useState<string>("");
  const [tokenInAmount, setTokenInAmount] = useState<string>("");

  const [tokenOut, setTokenOut] = useState<WrapTokenWithCurrency | undefined>();
  const [tokenOutBalance, setTokenOutBalance] = useState<string>("");
  const [tokenOutAmount, setTokenOutAmount] = useState<string>("");

  const [limit, setLimit] = useState<{
    min: number;
    max: number;
  }>(INITIAL_LIMIT);
  const [fee, setFee] = useState<string>("");

  const [disableText, setDisableText] = useState<string>("");

  const isInLimit = useMemo(() => {
    if (!tokenInAmount) return false;
    if (!limit) return false;
    if (new BigNumber(tokenInAmount).isGreaterThan(new BigNumber(limit.max))) {
      setDisableText('Max order limit exceeded')
      return false;
    }
    if (new BigNumber(tokenInAmount).isLessThan(new BigNumber(limit.min))) {
      setDisableText('Min order limit exceeded')
      return false;
    }
    setDisableText('')
    return true;
  }, [limit, tokenInAmount]);

  const getTokenAmountOut = useCallback(() => {
    if (!tokenInAmount) return;
    if (!tokenIn?.contractAddress) return;
    if (!tokenOut?.contractAddress) return;
    actionHandler(
      getTokenAmountsOutAction({
        amountIn: WEB3.toWei(tokenInAmount),
        inContractAddress: tokenIn?.contractAddress,
        outContractAddress: tokenOut?.contractAddress,
      }),
      (res: { amounts: string; totalNetworkFee: string }) => {
        setTokenOutAmount(formatNumber(WEB3.fromWei(res.amounts)));
        setFee(res.totalNetworkFee);
        setSwapState((prev) => ({ ...prev, isReady: true }));
      }
    );
  }, [tokenIn, tokenOut, tokenInAmount]);

  const getTokenAmountIn = useCallback(() => {
    if (!tokenOutAmount) return;
    if (!tokenIn?.contractAddress) return;
    if (!tokenOut?.contractAddress) return;
    actionHandler(
      getTokenAmountsInAction({
        amountOut: WEB3.toWei(tokenOutAmount),
        inContractAddress: tokenIn?.contractAddress,
        outContractAddress: tokenOut?.contractAddress,
      }),
      (res: { amounts: string; totalNetworkFee: string }) => {
        setTokenInAmount(formatNumber(WEB3.fromWei(res.amounts)));
        setFee(res.totalNetworkFee);
        setSwapState((prev) => ({ ...prev, isReady: true }));
      }
    );
  }, [tokenIn, tokenOut, tokenOutAmount]);

  const onSwap = useCallback(() => {
    if (!swapData || !tokenIn || !tokenOut || !tokenInAmount || !tokenOutAmount)
      return;
    actionHandler(
      makeSwapTransactionAction({
        tokenIn,
        tokenInAmount,
        tokenOut,
        tokenOutAmount,
        totalNetworkFee: fee,
      }),
      (result) => {
        setTokenIn(undefined);
        setTokenInAmount("");
        setTokenInBalance("");
        setTokenOut(undefined);
        setTokenOutAmount("");
        setTokenOutBalance("");
        setLimit(INITIAL_LIMIT)
        navigate('/history');
      }
    );
  }, [navigate, swapData, tokenIn, tokenInAmount, tokenOut, tokenOutAmount, fee]);

  // Effect
  useEffect(() => {
    if (!tokenIn || !tokenOut) {
      setLimit(INITIAL_LIMIT)
      return;
    }
    actionHandler(getSwapLimitAciton({ unit: tokenIn.unit, fromTokenId: tokenIn.wrapTokenId, toTokenId: tokenOut.wrapTokenId }), setLimit);
  }, [tokenIn, tokenOut]);

  const isOverTokenInBalance = useMemo(() => {
    const result = new BigNumber(WEB3.toWei(tokenInAmount)).isGreaterThan(new BigNumber(tokenInBalance))
    setDisableText(result ? 'Insufficient deposit balance' : '')
    return result;
  }, [tokenInAmount, tokenInBalance])

  return (
    <div id={"Swap"}>
      <div className="swap-top">
        <SwapCurrencyInput
          label="Deposit"
          wrapTokenList={swapData?.wrapTokens ?? []}
          wrapToken={tokenIn}
          onBlur={getTokenAmountOut}
          onSelect={(wrapToken: WrapToken & { currency: Currency }): void => {
            setTokenIn(wrapToken);
            setTokenInAmount("");

            setTokenOut(undefined);
            setTokenOutAmount("");
            setTokenInBalance("");
            setLimit(INITIAL_LIMIT)

            actionHandler(
              getSwapPairAction({ wrapTokenId: wrapToken.wrapTokenId }),
              setSwapPairs
            );
            actionHandler(
              getCurrencyBalanceAction({
                currency: wrapToken.currency,
                address: account.userInfo.new_address,
              }),
              setTokenInBalance
            );
          }}
          amount={tokenInAmount}
          onChangeAmount={(amount: string) => {
            setSwapState({ userInsert: "IN", isReady: false });
            setTokenInAmount(amount);
            setTokenOutAmount('');
          }}
          balance={tokenInBalance}
          notEnoughBalance={isOverTokenInBalance}
        />
        <div className="swap-between">
          <div className="swap-between-icon-container">
            <img src={downArrow} className="swap-between-icon" alt="" />
          </div>
        </div>
        <SwapCurrencyInput
          label="Receive"
          wrapTokenList={swapPairs}
          wrapToken={tokenOut}
          onBlur={getTokenAmountIn}
          onSelect={(wrapToken: WrapToken & { currency: Currency }): void => {
            setTokenOut(wrapToken);
            actionHandler(
              getCurrencyBalanceAction({
                currency: wrapToken.currency,
                address: account.userInfo.new_address,
              }),
              setTokenOutBalance
            );
          }}
          amount={tokenOutAmount}
          onChangeAmount={(amount: string) => {
            setSwapState({ userInsert: "OUT", isReady: false });
            setTokenOutAmount(amount);
            setTokenInAmount('');
          }}
          balance={tokenOutBalance}
        />
        {/* {tokenIn?.wrapTokenId && tokenOut?.wrapTokenId && (
          <div className="swap-ratio">
            <img src={infoIcon} className="swap-ratio-icon" alt="" />
            <span className="swap-ratio-text">
              1 {tokenIn?.unit} = 0.0000058 {tokenOut?.unit}{" "}
              <span className="swap-ratio-value">$1,321</span>
            </span>
          </div>
        )} */}
      </div>
      <div className="swap-bottom">
        {disableText && <p className={'limit-info'}> {disableText}</p>}
        <div className="swap-info">
          <div className="swap-info-row">
            <span className="swap-info-label">Max order</span>
            <span className="swap-info-value">
              {formatNumber(limit.max)} {tokenIn?.unit}
            </span>
          </div>
          <div className="swap-info-line" />
          <div className="swap-info-row">
            <span className="swap-info-label">Min order</span>
            <span className="swap-info-value">
              {formatNumber(limit.min) ?? "-"} {tokenIn?.unit}
            </span>
          </div>
          <div className="swap-info-line" />
          <div className="swap-info-row">
            <span className="swap-info-label">Estimated fee</span>
            <span className="swap-info-value">
              {formatNumber(WEB3.fromWei(fee))} {tokenIn?.unit}
            </span>
          </div>
        </div>
        <p className="subtext">
          Token Swap Service is exclusively provided by Acroswap, a third party
          swap service provider. For details, please refer to the link of&nbsp;
          <a
            className="subtext-link"
            href="https://acroswap.com"
            target="_blank"
            rel="noreferrer"
          >
            Acroswap
          </a>
        </p>
        <button
          className="swap-button"
          onClick={onSwap}
          disabled={!swapState.isReady || !isInLimit || isOverTokenInBalance}
        >
          Swap
        </button>
      </div>
    </div>
  );
}

export default Swap;
